import {
  AI_PREDICTION_OUTPUT_EVENT,
  AI_PREDICTION_USER_FEEDBACK_EVENT,
  HELPFUL,
  IMPLICIT_BAD,
  IMPLICIT_GOOD,
  NOT_HELPFUL,
  USER_INTERACTION_EVENT,
  ZSL_EXECUTION_ERROR,
} from "@zapier/canvas-constants";
import { atom } from "jotai";

import { uuid } from "../utils";
import { canvasIdAtom } from "../canvas";
import { currentAccountIdAtom, sessionAtom } from "../account";
import { isTemplatePageAtom } from "../templates";

declare global {
  interface Window {
    EventToolkit: {
      emit(options: Record<string, any>, event: string): any;
    };
  }
}

const commonPropsAtom = atom((get) => {
  const canvasId = get(canvasIdAtom);
  const session = get(sessionAtom);
  const currentAccountId = get(currentAccountIdAtom);
  return {
    system_name: "canvas",
    canvas_id: canvasId,
    customuser_id: session?.id,
    account_id: currentAccountId,
  };
});
commonPropsAtom.debugLabel = "commonPropsAtom";

type PredictionOutputEventPayload = {
  prediction_id: string;
  prediction_source: string;
  prediction_json_input: string;
  prediction_json_output: string;
  correlation_id?: string | null;
  model_name?: string;
};

export const emitAiPredictionOutputEventSetter = atom(
  null,
  (get, _set, payload: PredictionOutputEventPayload) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      window.EventToolkit.emit(
        {
          model_provider_name: "openai",
          model_name: "gpt-4o",
          ...payload,
          ...commonProps,
        },
        AI_PREDICTION_OUTPUT_EVENT,
      );
    }
  },
);
emitAiPredictionOutputEventSetter.debugLabel =
  "emitAiPredictionOutputEventSetter";

type PredictionUserFeedbackEventPayload = {
  prediction_id: string;
  user_rating:
    | typeof HELPFUL
    | typeof NOT_HELPFUL
    | typeof IMPLICIT_BAD
    | typeof IMPLICIT_GOOD
    | null;
  user_feedback: string | null;
  correlation_id?: string | null;
};

export const emitAiPredictionUserFeedbackEventSetter = atom(
  null,
  (get, _set, payload: PredictionUserFeedbackEventPayload) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      window.EventToolkit.emit(
        {
          ...payload,
          user_feedback: payload.user_feedback || null,
          ...commonProps,
        },
        AI_PREDICTION_USER_FEEDBACK_EVENT,
      );
    }
  },
);
emitAiPredictionUserFeedbackEventSetter.debugLabel =
  "emitAiPredictionUserFeedbackEventSetter";

export const emitEventOnAICopilotStartSetter = atom(null, (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "click",
        interaction_goal: "AI Copilot start",
        interaction_raw_text: "Start with AI Copilot",
        option_selected: "Role Specific Prompt",
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnAICopilotStartSetter.debugLabel = "emitEventOnAICopilotStartSetter";

export const emitEventOnAIExecutionSetter = atom(null, (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "click",
        interaction_goal: "AI Execution",
        interaction_raw_text: "Use this system",
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnAIExecutionSetter.debugLabel = "emitEventOnAIExecutionSetter";

export const emitEventOnChatbotsPlanUpgradeSetter = atom(
  null,
  (get, _set, rawText: string) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "upgrade Chatbots",
          interaction_raw_text: rawText,
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  },
);
emitEventOnChatbotsPlanUpgradeSetter.debugLabel =
  "emitEventOnChatbotsPlanUpgradeSetter";

export const emitEventOnExecuteZslSetter = atom(
  null,
  (get, _set, slug: string) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "Execute template",
          interaction_raw_text: slug,
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  },
);
emitEventOnExecuteZslSetter.debugLabel = "emitEventOnExecuteZslSetter";

export const emitEventOnInterfacesPlanUpgradeSetter = atom(
  null,
  (get, _set, rawText: string) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "upgrade Interfaces",
          interaction_raw_text: rawText,
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  },
);
emitEventOnInterfacesPlanUpgradeSetter.debugLabel =
  "emitEventOnInterfacesPlanUpgradeSetter";

export const emitEventOnNewCanvasCreationSetter = atom(
  null,
  async (get, _set, rawText?: string) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      await window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "Create Canvas",
          interaction_raw_text: rawText || "Create canvas",
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  },
);
emitEventOnNewCanvasCreationSetter.debugLabel =
  "emitEventOnNewCanvasCreationSetter";

export const emitEventOnPromptButtonClickSetter = atom(
  null,
  (
    get,
    _set,
    {
      rawText,
      optionSelected,
      interactionGoal,
    }: { interactionGoal?: string; rawText: string; optionSelected: string },
  ) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: interactionGoal ?? "AI Copilot prompt button click",
          interaction_raw_text: rawText,
          option_selected: optionSelected,
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  },
);
emitEventOnPromptButtonClickSetter.debugLabel =
  "emitEventOnPromptButtonClickSetter";

export const emitEventOnStartFromScratchSetter = atom(null, (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "click",
        interaction_goal: "Start from scratch or template",
        interaction_raw_text: "Start from scratch or template",
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnStartFromScratchSetter.debugLabel =
  "emitEventOnStartFromScratchSetter";

export const emitEventOnStartingOverSetter = atom(null, async (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    await window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "click",
        interaction_goal: "Start over from AI",
        interaction_raw_text: "Start over",
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnStartingOverSetter.debugLabel = "emitEventOnStartingOverSetter";

export const emitEventOnSuggestStepSetter = atom(null, (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "click",
        interaction_goal: "Suggest step",
        interaction_raw_text: "Suggest step",
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnSuggestStepSetter.debugLabel = "emitEventOnSuggestStepSetter";

export const emitEventOnTablesPlanUpgradeSetter = atom(
  null,
  (get, _set, rawText: string) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "upgrade Tables",
          interaction_raw_text: rawText,
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  },
);
emitEventOnTablesPlanUpgradeSetter.debugLabel =
  "emitEventOnTablesPlanUpgradeSetter";

export const emitEventOnTemplateToolbarCloseSetter = atom(null, (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "click",
        interaction_goal: "Template toolbar closed",
        interaction_raw_text: "Close template toolbar",
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnTemplateToolbarCloseSetter.debugLabel =
  "emitEventOnTemplateToolbarCloseSetter";

export const emitEventOnUpgradeBannerMountSetter = atom(null, (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    const isTemplatePage = get(isTemplatePageAtom);

    window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "view",
        interaction_goal: "upgrade banner mount",
        interaction_raw_text: `Upgrade banner shown on ${isTemplatePage ? "template" : "non-template"} page`,
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnUpgradeBannerMountSetter.debugLabel =
  "emitEventOnUpgradeBannerMountSetter";

export const emitEventOnWelcomeModalCloseSetter = atom(null, (get) => {
  if (window.EventToolkit?.emit) {
    const commonProps = get(commonPropsAtom);
    window.EventToolkit.emit(
      {
        event_id: uuid(),
        event_action: "click",
        interaction_goal: "Welcome modal closed",
        interaction_raw_text: "Cancel",
        ...commonProps,
      },
      USER_INTERACTION_EVENT,
    );
  }
});
emitEventOnWelcomeModalCloseSetter.debugLabel =
  "emitEventOnWelcomeModalCloseSetter";

export const emitUserInteractionEventSetter = atom(
  null,
  async (
    get,
    _set,
    {
      eventAction,
      interactionGoal,
      rawText,
      optionSelected,
    }: {
      eventAction: string;
      interactionGoal: string;
      rawText?: string;
      optionSelected?: string;
    },
  ) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);
      await window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: eventAction,
          interaction_goal: interactionGoal,
          interaction_raw_text: rawText,
          option_selected: optionSelected,
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  },
);
emitUserInteractionEventSetter.debugLabel = "emitUserInteractionEventSetter";

type ZslExecutionErrorEventParams = {
  prompt: string;
  zsl: string;
  status_code: number;
  error_message: string;
  is_ai_page: boolean;
  is_template_page: boolean;
};

export const emitZslExecutionErrorEventSetter = atom(
  null,
  (
    get,
    _set,
    {
      error_message,
      prompt,
      zsl,
      status_code,
      is_ai_page,
      is_template_page,
    }: ZslExecutionErrorEventParams,
  ) => {
    if (window.EventToolkit?.emit) {
      const commonProps = get(commonPropsAtom);

      window.EventToolkit.emit(
        {
          event_id: uuid(),
          origin_system_name: "canvas",
          prompt,
          zsl,
          status_code,
          error_message,
          is_ai_page,
          is_template_page,
          ...commonProps,
        },
        ZSL_EXECUTION_ERROR,
      );
    }
  },
);
emitZslExecutionErrorEventSetter.debugLabel =
  "emitZslExecutionErrorEventSetter";
